import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import '../../Roles/roles.css';
import '../organizations.css';
import { OWNER_SEARCH_DEBOUNCE_MS } from '../../../../constants/organization';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { useStationSearchQuery } from '../../../../hooks/useDashboardQueries';
import { StationV2 } from '../../../../types/dashboardV2';

interface AttachStationDialogProps {
  open: boolean;
  organizationId: string;
  onClose: () => void;
  onAttach: (stationId: string) => Promise<void>;
  isSubmitting: boolean;
  errorMessage?: string;
}

export const AttachStationDialog = ({
  open,
  organizationId,
  onClose,
  onAttach,
  isSubmitting,
  errorMessage,
}: AttachStationDialogProps) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [pendingMove, setPendingMove] = useState<StationV2 | null>(null);
  const debouncedTerm = useDebouncedValue(term, OWNER_SEARCH_DEBOUNCE_MS);
  const { data } = useStationSearchQuery(debouncedTerm, open);

  // Stations already in this organisation are on the page behind the dialog.
  const options: StationV2[] = (data?.data ?? []).filter(
    (s: StationV2) => s.organizationId !== organizationId,
  );

  const choose = (station: StationV2) => {
    if (station.organizationId) {
      setPendingMove(station);   // belongs elsewhere — confirm the move
      return;
    }
    void onAttach(station.id);
  };

  if (pendingMove) {
    return (
      <Dialog open onClose={() => setPendingMove(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('dashboard.organizationStations.attachConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('dashboard.organizationStations.attachReassignWarning', {
              idTag: pendingMove.idTag,
              currentName:
                pendingMove.organizationName ??
                t('dashboard.organizationStations.unassigned'),
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <button type="button" className="secondary-action-btn" onClick={() => setPendingMove(null)} disabled={isSubmitting}>
            {t('dashboard.common.cancel')}
          </button>
          <button
            type="button"
            className="primary-action-btn"
            onClick={() => {
              const id = pendingMove.id;
              setPendingMove(null);
              void onAttach(id);
            }}
            disabled={isSubmitting}
          >
            {t('dashboard.organizationStations.attach')}
          </button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('dashboard.organizationStations.attachTitle')}</DialogTitle>
      <DialogContent>
        <input
          type="text"
          className="filter-search-input"
          placeholder={t('dashboard.organizationStations.attachSearchPlaceholder')}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label={t('dashboard.organizationStations.attachSearchPlaceholder')}
        />
        {options.length === 0 ? (
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('dashboard.organizationStations.attachNoResults')}
          </Typography>
        ) : (
          <ul className="owner-search-options" style={{ marginTop: 12 }}>
            {options.map((station) => (
              <li key={station.id}>
                <button
                  type="button"
                  className="owner-search-option"
                  onClick={() => choose(station)}
                  disabled={isSubmitting}
                >
                  <strong>{station.idTag}</strong> — {station.address}
                  {station.organizationId && (
                    <span className="org-text-muted" style={{ display: 'block', fontSize: 12 }}>
                      {t('dashboard.organizationStations.currentlyIn', {
                        name: station.organizationName ?? station.organizationId,
                      })}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {errorMessage && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }} role="alert">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <button type="button" className="secondary-action-btn" onClick={onClose} disabled={isSubmitting}>
          {t('dashboard.common.cancel')}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachStationDialog;
